import { z } from 'zod';
import { id, paginatedResult, paginationQuery } from './common.js';

export const payoutSetupStatus = z.enum([
  'requested',
  'in_progress',
  'submitted',
  'in_review',
  'needs_attention',
  'reviewed',
  'closed',
]);
export const payoutProgress = z.object({
  providerAccountCreated: z.boolean(),
  taxInformationSubmitted: z.boolean(),
  paymentAccountAdded: z.boolean(),
});
export const payoutSetupProfile = z.object({
  id,
  professionalId: id,
  professionalName: z.string(),
  status: payoutSetupStatus,
  revision: z.number().int().positive(),
  progress: payoutProgress,
  workerReference: z.string().nullable(),
  operatorMessage: z.string().nullable(),
  requestedAt: z.string(),
  updatedAt: z.string(),
  reviewedAt: z.string().nullable(),
  provider: z.literal('workmarket'),
  evidenceSource: z.literal('self_report_and_operator_review'),
});
export const payoutSetupEvent = z.object({
  id,
  action: z.string(),
  actorName: z.string(),
  actorSide: z.enum(['professional', 'operator']),
  body: z.string(),
  recordedAt: z.string(),
  revision: z.number().int().positive(),
});
export const payoutSetupView = z.object({
  profile: payoutSetupProfile.nullable(),
  canWrite: z.boolean(),
  allowedActions: z.array(
    z.enum([
      'request',
      'reopen',
      'save_progress',
      'submit',
      'prepare',
      'start_review',
      'request_changes',
      'review',
      'close',
    ])
  ),
  providerConnected: z.literal(false),
  automatedVerificationAvailable: z.literal(false),
  providerPortalUrl: z.literal('https://www.workmarket.com/login'),
  taxHelpUrl: z.literal(
    'https://workmarket.zendesk.com/hc/en-us/articles/360018569734-Submitting-my-Tax-Information'
  ),
  paymentHelpUrl: z.literal(
    'https://workmarket.zendesk.com/hc/en-us/articles/360018824933-Adding-a-payment-account-to-withdraw-funds'
  ),
  notice: z.string(),
});
export const payoutSetupHistory = paginatedResult(payoutSetupEvent);
export const payoutSetupPage = paginatedResult(payoutSetupProfile);
export const payoutSetupQuery = paginationQuery
  .extend({
    search: z.string().trim().max(100).optional(),
    status: payoutSetupStatus.optional(),
  })
  .strict();
const requestFields = {
  revision: z.number().int().nonnegative(),
  clientRequestId: z.string().uuid(),
};
export const payoutSetupActionInput = z.discriminatedUnion('action', [
  z.object({ ...requestFields, action: z.literal('request') }).strict(),
  z.object({ ...requestFields, action: z.literal('reopen') }).strict(),
  z
    .object({ ...requestFields, action: z.literal('save_progress'), progress: payoutProgress })
    .strict(),
  z
    .object({
      ...requestFields,
      action: z.literal('submit'),
      progress: payoutProgress,
      acknowledgement: z.literal(true),
    })
    .strict(),
]);
export const payoutSetupAdminActionInput = z
  .object({
    ...requestFields,
    action: z.enum(['prepare', 'start_review', 'request_changes', 'review', 'close']),
    body: z.string().trim().min(10).max(1500),
    workerReference: z
      .string()
      .trim()
      .regex(/^[A-Za-z0-9_-]{1,100}$/, 'Use the provider worker reference only.')
      .nullable()
      .optional(),
  })
  .strict();
export type PayoutSetupStatus = z.infer<typeof payoutSetupStatus>;
export type PayoutProgress = z.infer<typeof payoutProgress>;
export type PayoutSetupProfile = z.infer<typeof payoutSetupProfile>;
export type PayoutSetupView = z.infer<typeof payoutSetupView>;
export type PayoutSetupEvent = z.infer<typeof payoutSetupEvent>;
export type PayoutSetupQuery = z.infer<typeof payoutSetupQuery>;
export type PayoutSetupActionInput = z.infer<typeof payoutSetupActionInput>;
export type PayoutSetupAdminActionInput = z.infer<typeof payoutSetupAdminActionInput>;
